import { defineComponent as _defineComponent } from 'vue'
import { Vue } from "vue-property-decorator"
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { CircularLoader } from "@coredination/components"
import { v4 as generateUuid } from "uuid"
import { User } from "@coredination/common"
import { navbar } from "@/components/NavbarOptions.vue"
import * as cdn from "@coredination/components"
import UpdateWorkReport from "@/graphql/workreport/updateworkreport.graphql"
import moment from "moment"
import modaldialog from "@/components/dialogs/modaldialog"
import DateField from "@/components/fields/DateField.vue"
import GroupField from "@/components/fields/GroupField.vue"
import ActivityField from "@/components/fields/ActivityField.vue"
import FreeTextField from "@/components/fields/FreeTextField.vue"
import TimeField from "@/components/fields/TimeField.vue"
import DurationField from "@/components/fields/DurationField.vue"
import TemplateSelectField from "@/components/fields/TemplateSelectField.vue"

import WorkReportQuery from "@/graphql/workreport/workreport.graphql"

export default /*#__PURE__*/_defineComponent({
  __name: 'AbsenceReportEdit',
  props: {
    new: { type: Boolean },
    uuid: { type: String }
},
  setup(__props) {

const props = __props

const instance = getCurrentInstance()


const { result: absenceReportResult, onResult: onAbsenceReportResult, refetch: refetchAbsenceReportResult, loading: loadingAbsenceReport } = useQuery(WorkReportQuery, () =>
    ({
        uuid: props.uuid
    }), {
        enabled: !!props.uuid
    }
)
onAbsenceReportResult((response) => {
	if (!response?.data?.workReport) return
    absenceReport.value = JSON.parse(JSON.stringify(response.data.workReport))
})
const absenceReport = ref({
    activityId: null,
    description: "",
    endTime: null,
    excludedFields: [],
    fromTemplateId: null,
    groupUuid: null,
    requiredFields: [],
    startTime: null,
    type: "ABSENCE",
    uuid: generateUuid(),
    workDate: new Date().getTime(),
    workDuration: null,
})

const route = useRoute();
const router = useRouter();
const loading = computed(() => { loadingAbsenceReport.value })
const workDuration = ref(0)
const workDurationUpdateKey = ref(0)
const errorRequiredFields = ref<string[]>([]);

onMounted(() => {
    navbar?.setOptions([{ icon: "done", action: save }])
    const params: any = {
            ...route.params,
            ...route.query,
    }
    if ("date" in params) absenceReport.value.workDate = moment(params.date).toDate().getTime()
})

const handleTemplateChange = () => {
    errorRequiredFields.value = []
    if (absenceReport && absenceReport.value && !absenceReport.value.workDate) {
        absenceReport.value.workDate = new Date().getTime()
    }
}

const attachTimeToReportDate = (time: number | Date): number => {
    var workDate: Date = absenceReport.value.workDate ? new Date(absenceReport.value.workDate) : new Date()
    var timeDate: Date = new Date(time)
    workDate.setHours(timeDate.getHours())
    workDate.setMinutes(timeDate.getMinutes())
    return workDate.getTime()
}

const changeStartEnd = () => {
    if (absenceReport.value && absenceReport.value.startTime && absenceReport.value.endTime) {
        var duration: number = (absenceReport.value.endTime - absenceReport.value.startTime) / 60000

        // round up to nearest X minutes, according to group conf or 30 by default
        var rounding: number = 30
        var roundUpwards: boolean = false
        var minimum: number = 0

        // Skipping group config for now, just to have something working
        /*
        var groupConfiguration: any = this.groupConfiguration
        if (groupConfiguration) {
            rounding = parseInt(groupConfiguration.work_duration_rounding!)
            minimum = parseInt(groupConfiguration.min_work_duration!)
            roundUpwards = groupConfiguration.always_round_upwards! === "true"
        }
        */
        if (rounding > 0) {
            var mod: number = duration % rounding
            duration += (roundUpwards && mod > 0) || mod >= rounding / 2 ? rounding - mod : -mod
        }
        if (duration < minimum) duration = minimum
        ;(workDuration.value as any).changeSilent(duration)
        workDurationUpdateKey.value++
    }
}

const required = computed(() => {
    return absenceReport.value && absenceReport.value.requiredFields
        ? absenceReport.value.requiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
        : null
})

const { mutate: updateAbsenceReportMutation } = useMutation(UpdateWorkReport)
const save = async () => {
    if (!absenceReport.value.uuid) absenceReport.value.uuid = generateUuid()
    const input: any = {
        activityId: absenceReport.value.activityId || null,
        description: absenceReport.value.description && absenceReport.value.description.trim().length > 0 ? absenceReport.value.description : null,
        endTime: absenceReport.value.endTime ? attachTimeToReportDate(absenceReport.value.endTime) : null,
        fromTemplateId: absenceReport.value.fromTemplateId || null,
        groupUuid: absenceReport.value.groupUuid || null,
        requiredFields: absenceReport.value.requiredFields || [],
        startTime: absenceReport.value.startTime ? attachTimeToReportDate(absenceReport.value.startTime) : null,
        type: absenceReport.value.type || null,
        workDate: absenceReport.value.workDate || null,
        workDuration: absenceReport.value.workDuration || null,
    }
    if (!verifyRequired(input)) {
        if (instance) instance.proxy.$toast.error(instance.proxy.$t("required_field_error") as string, { icon: 'warning' });
        return
    }
    const response = await updateAbsenceReportMutation({
        uuid: absenceReport.value.uuid,
        input: input
    }, {
        update(proxy: any, response: any) {
            refetchAbsenceReportResult()
        }
    }).then(async (response: any) => {
        absenceReport.value = response.data.updateWorkReport
        router.back()
    })
}

const verifyRequired = (input: any) => {
    errorRequiredFields.value = []
    if (input.requiredFields) {
        if (input.requiredFields.includes("activity"))
            if (!input["activityId"]) errorRequiredFields.value.push("activity")
        if (input.requiredFields.includes("description"))
            if (!input["description"]) errorRequiredFields.value.push("description")
        if (input.requiredFields.includes("group"))
            if (!input["groupUuid"]) errorRequiredFields.value.push("group")
        if (input.requiredFields.includes("startTime"))
            if (!input["startTime"]) errorRequiredFields.value.push("startTime")
        if (input.requiredFields.includes("endTime"))
            if (!input["endTime"]) errorRequiredFields.value.push("endTime")
        if (input.requiredFields.includes("workDuration"))
            if (!input["workDuration"]) errorRequiredFields.value.push("workDuration")
    }
    if (errorRequiredFields.value.length == 0) return true
    return false
}

const clearErrorRequired = (field: string) => {
    errorRequiredFields.value = errorRequiredFields.value.filter((e) => e !== field)
}

const errorRequired = computed(() => {
    return errorRequiredFields.value
        ? errorRequiredFields.value.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
        : null
})

const excluded = computed(() => {
    return absenceReport.value && absenceReport.value.excludedFields
        ? absenceReport.value.excludedFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
        : null
})

return { __sfc: true,instance, props, absenceReportResult, onAbsenceReportResult, refetchAbsenceReportResult, loadingAbsenceReport, absenceReport, route, router, loading, workDuration, workDurationUpdateKey, errorRequiredFields, handleTemplateChange, attachTimeToReportDate, changeStartEnd, required, updateAbsenceReportMutation, save, verifyRequired, clearErrorRequired, errorRequired, excluded, CircularLoader, DateField, GroupField, ActivityField, FreeTextField, TimeField, DurationField, TemplateSelectField }
}

})